import { _ } from "../../../core/polyglot/polyglot.js";
import accountingDocument from "../../shared/accountingDocument.js";
import CustomPreset from "../../../models/keyFigure/preset/CustomPreset.js";
import getKeyFigureService from "../../../core/services/getKeyFigureService.js";
import keyFigurePresetPickerWidget from "./keyFigurePresetPickerWidget/keyFigurePresetPickerWidget.js";
import KeyFigurePresetService from "../../../core/services/KeyFigurePresetService.js";
import KeyFigureResultService from "../../../core/services/KeyFigureResultService.js";
import previousPeriodPickerWidget from "./actions/previousPeriodPickerWidget.js";
import session from "../../../core/session.js";
import SortableKeyFigureListEditor from "../../../core/widgets/keyFigures/SortableKeyFigureListEditor/SortableKeyFigureListEditor.js";
import visibleKeyFigures from "../../../models/keyFigure/preset/visibleKeyFigures.js";

/**
 * `abstractKeyFigureDocument` is a common abstract superclass to keyFigure reports
 * and multiple keyFigures, since they share a lot of logic (they display the same
 * information in two different ways)
 *
 * @param {object} spec - Parameter object
 * @param {object} my - protected fields holder
 * @return {*}
 */
function abstractKeyFigureDocument(spec, my) {
	spec ||= {};
	my ||= {};
	const that = accountingDocument(spec, my);

	// Todo: Examine the possibility of having a `my.currentUser` and `my.currentCompany`
	const user = session.getCurrentUser();
	const company = session.getCompany();

	const canEditPresets = user.canEditPresetsFor(company);

	my.customPreset = new CustomPreset();
	my.location = "";

	my.keyFigureResultService = new KeyFigureResultService({
		companyId: session.getCompanyId(),
		keyFigureService: getKeyFigureService(),
	});

	my.keyFigurePresetService = new KeyFigurePresetService({
		companyId: session.getCompanyId(),
		keyFigureService: getKeyFigureService(),
	});

	my.previousPeriodPicker = previousPeriodPickerWidget();

	my.previousPeriodPicker.onPeriodChange((element) => {
		my.setNumberOfPeriods(element.value);
	});

	my.sortableKeyFigureList = new SortableKeyFigureListEditor({
		onListUpdated: (keyFigures) => my.keyFigureChanged(keyFigures),
		onListSorted: (keyFigures) => my.switchToCustomPreset(keyFigures),
	});

	my.presetEditor = keyFigurePresetPickerWidget({
		enableEditor: canEditPresets,
		currentCompany: company,
		customPreset: my.customPreset,
	});

	my.presetEditor.onPresetChange.register((preset) => {
		my.presetChanged();
		my.redirectTo(my.location, { preset: preset.getId() });
	});

	my.presetEditor.onPresetCreated.register(async (preset) => {
		await my.keyFigurePresetService.save(preset);
		my.customPreset = new CustomPreset();
		my.presetEditor.setCustomPreset(my.customPreset);
		my.redirectTo(my.location, { preset: preset.getId() });
	});

	my.presetEditor.onPresetEdited.register(async (preset) => {
		await my.keyFigurePresetService.update(preset);

		my.presetEditor.getPresets().forEach((element) => {
			if (element.id !== preset.getId()) {
				return;
			}
			element.name = preset.getName();
		});

		my.presetEditor.update();
	});

	my.presetEditor.onPresetRemoved.register(async (preset) => {
		await my.keyFigurePresetService.remove(preset.getId());
		my.redirectTo(my.location, {});
	});

	that.actions = [
		my.rightMenuSection(
			_("Number of previous periods"),
			my.previousPeriodPicker,
		),
		my.rightMenuSection(_("Presets"), my.presetEditor),

		my.rightMenuSection("", (html) => {
			html.div(
				{ "data-help": "keyfigure-picker" },
				my.sortableKeyFigureList,
			);
		}),
	];

	/**
	 * Entry point - Show the keyFigures contained in the preset
	 * provided as argument (as an ID)
	 *
	 * @param {string} selectedPresetId - Identifier of the preset to show
	 */
	that.showKeyFigures = function (selectedPresetId) {
		my.showKeyFigures(selectedPresetId);
	};

	/**
	 * Hook function called when the number of periods to show changed
	 */
	my.setNumberOfPeriods = function (_number) {
		throw "Should be overridden";
	};

	/**
	 * Hook function called when the set of keyFigures to display changed
	 *
	 * @param {KeyFigure[]} keyFigures - List of changed keyFigures
	 */
	my.keyFigureChanged = function (_keyFigures) {
		throw "Should be overridden";
	};

	/**
	 * Used to set the content of the document subwidgets (not the actions)
	 */
	my.show = function () {
		throw "Should be overridden";
	};

	/**
	 * Hook function called when the preset changed,
	 * and before the redirection is made
	 */
	my.presetChanged = function () {
		throw "Should be overridden";
	};

	/**
	 * Switch the current view to a custom preset.
	 * The argument is used to fill the custom preset
	 *
	 * @param {KeyFigure[]} keyFigures - List of KeyFigures to show
	 */
	my.switchToCustomPreset = function (keyFigures) {
		my.customPreset.setKeyFigures(keyFigures);
		my.redirectTo(my.location, { preset: my.customPreset.getId() });
	};

	/**
	 * Entry point to populate all the subwidgets
	 * by doing queries to the server, populating the preset picker etc
	 *
	 * @param {string} selectedPresetId - Identifier of the preset to show
	 */
	my.showKeyFigures = async function (selectedPresetId) {
		const presets = await my.keyFigurePresetService.findAllVisiblePresets();

		presets.push(my.customPreset);
		const presetCategories = my.presetEditor.setPresets(
			presets,
			selectedPresetId,
		);
		my.setPicker(presetCategories);
	};

	/**
	 * Set the content of the preset picker, select the default entry, and
	 * finally populate the document subwidgets (chart, legends, tables, etc)
	 *
	 * @param {Map<string, Preset>} presetsMap - Map of presets, with their ID as key
	 */
	my.setPicker = function (presetsMap) {
		const name = my.presetEditor.getSelected()?.getName();
		const myKeyFigurePreset = presetsMap[name];
		if (myKeyFigurePreset) {
			my.sortableKeyFigureList.setup({
				myKeyFigures: myKeyFigurePreset.getItems(),
				allKeyFigures: visibleKeyFigures.getKeyFigures(),
			});
		}
		my.show();
	};

	my.getVisibleKeyFigures = function () {
		return my.sortableKeyFigureList
			.getSelectedItems()
			.filter((keyFigure) => keyFigure.isVisible());
	};

	return that;
}

export default abstractKeyFigureDocument;
